import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import CertificateListPage from '@/shein-platform/modules/certificates/CertificateListPage';
import ComplianceDashboard from '@/shein-platform/modules/compliance/ComplianceDashboard';
import SkcLabelPanel from '@/shein-platform/modules/compliance/SkcLabelPanel';
import '@/styles/shein-platform/shein-product-ops.css';

const DIM_PHOTO = 'photo';
const DIM_LABEL = 'label';
const DIM_AGENCY = 'agency';
const DIM_CERTIFICATE = 'certificate';

/** 合规维度共用同步 kind：切维度不卸载，进度认领不丢 */
const PRODUCT_QUAL_SYNC_KINDS = ['certificate', 'compliance', 'skcAgency'];

const DIM_TABS = [
  { id: DIM_PHOTO, label: '实拍图' },
  { id: DIM_LABEL, label: '合规标签' },
  { id: DIM_AGENCY, label: '代理绑定' },
  { id: DIM_CERTIFICATE, label: '证书绑定' },
];

const PHOTO_FILTERS = new Set(['pending', 'reviewing', 'approved', 'active', 'all']);
const AGENCY_FILTERS = new Set(['all', 'unbound', 'bound']);

function resolveDim(raw) {
  if (raw === DIM_CERTIFICATE || raw === 'cert') return DIM_CERTIFICATE;
  if (raw === DIM_AGENCY || raw === 'company' || raw === 'bind-company') return DIM_AGENCY;
  if (raw === DIM_LABEL || raw === 'tag' || raw === 'print-label') return DIM_LABEL;
  return DIM_PHOTO;
}

function resolvePhotoFilter(raw) {
  if (raw === 'active') return 'approved';
  if (raw === 'pending' || raw === 'reviewing' || raw === 'approved' || raw === 'all') return raw;
  return 'all';
}

export default function ProductQualificationPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const dimFromUrl = resolveDim(searchParams.get('dim'));
  const filterFromUrl = searchParams.get('filter') || '';
  const bindFromUrl = searchParams.get('bindStatus') || '';

  const [dim, setDim] = useState(dimFromUrl);

  useEffect(() => {
    setDim(dimFromUrl);
  }, [dimFromUrl]);

  const certInitialFilter = useMemo(() => {
    if (filterFromUrl === '0' || filterFromUrl === '1' || filterFromUrl === '2' || filterFromUrl === '3') {
      return filterFromUrl;
    }
    if (filterFromUrl === 'todo' || filterFromUrl === 'missing' || filterFromUrl === 'required') return '0';
    if (filterFromUrl === 'pending') return '1';
    if (filterFromUrl === 'approved') return '2';
    if (filterFromUrl === 'rejected') return '3';
    if (filterFromUrl === 'all') return 'all';
    return 'all';
  }, [filterFromUrl]);

  const photoDefaultFilter = resolvePhotoFilter(filterFromUrl);

  const switchDim = (next) => {
    setDim(next);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('dim', next);
    if (next === DIM_CERTIFICATE) {
      nextParams.delete('bindStatus');
      if (!nextParams.get('filter') || PHOTO_FILTERS.has(nextParams.get('filter')) || AGENCY_FILTERS.has(nextParams.get('filter'))) {
        nextParams.set('filter', 'all');
      }
    } else if (next === DIM_AGENCY) {
      nextParams.delete('filter');
      if (!AGENCY_FILTERS.has(nextParams.get('bindStatus') || '')) {
        nextParams.set('bindStatus', AGENCY_FILTERS.has(bindFromUrl) ? bindFromUrl : 'unbound');
      }
    } else if (next === DIM_LABEL) {
      nextParams.delete('bindStatus');
      nextParams.delete('filter');
    } else {
      nextParams.delete('bindStatus');
      if (!PHOTO_FILTERS.has(nextParams.get('filter') || '')) {
        nextParams.set('filter', 'all');
      }
    }
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <div className="spo-dim-page">
      <div className="spo-status-tabs spo-dim-tabs" role="tablist" aria-label={fixedUi("产品合规维度")}>
        {DIM_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={dim === tab.id}
            className={`spo-status-tab${dim === tab.id ? ' spo-status-tab--active' : ''}`}
            onClick={() => switchDim(tab.id)}
          >
            {fixedUi(tab.label)}
          </button>
        ))}
      </div>
      <div
        className="spo-dim-page__panel"
        hidden={dim !== DIM_PHOTO}
        aria-hidden={dim !== DIM_PHOTO}
      >
        <SkcLabelPanel
          mode="photo"
          active={dim === DIM_PHOTO}
          defaultReviewFilter={photoDefaultFilter}
          syncKinds={PRODUCT_QUAL_SYNC_KINDS}
        />
      </div>
      <div
        className="spo-dim-page__panel"
        hidden={dim !== DIM_LABEL}
        aria-hidden={dim !== DIM_LABEL}
      >
        <SkcLabelPanel
          mode="label"
          active={dim === DIM_LABEL}
          syncKinds={PRODUCT_QUAL_SYNC_KINDS}
        />
      </div>
      <div
        className="spo-dim-page__panel"
        hidden={dim !== DIM_AGENCY}
        aria-hidden={dim !== DIM_AGENCY}
      >
        <ComplianceDashboard
          active={dim === DIM_AGENCY}
          syncKinds={PRODUCT_QUAL_SYNC_KINDS}
        />
      </div>
      <div
        className="spo-dim-page__panel"
        hidden={dim !== DIM_CERTIFICATE}
        aria-hidden={dim !== DIM_CERTIFICATE}
      >
        <CertificateListPage
          embedded
          active={dim === DIM_CERTIFICATE}
          initialFilter={certInitialFilter}
          syncKinds={PRODUCT_QUAL_SYNC_KINDS}
        />
      </div>
    </div>
  );
}
